import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import ApiService from '../../../../../../services/ApiService';
import ConfirmationModal from '../../../../../../components/ModalConfirmation';
import { SITUACAO_LABEL, SITUACAO_TOM, emissorLabel, extractErrorMessage, usoPartes, usoTotal } from '../constants';
import { Chip } from '../style';
import { EmissorResumo, NcmProfileItemDto } from '../types';
import RuleTab from './RuleTab';
import LinksTab from './LinksTab';
import {
  Body,
  Breadcrumb,
  Chips,
  CloseButton,
  Count,
  Description,
  Footer,
  Header,
  HeaderTop,
  NcmCode,
  Panel,
  PushRightButton,
  Scrim,
  Tab,
  Tabs,
} from './style';

export type NcmDrawerTab = 'regra' | 'vinculos';

interface Props {
  item: NcmProfileItemDto;
  emissores: EmissorResumo[];
  adotados: { ncmId: number; ncm: string }[];
  tab: NcmDrawerTab;
  onTabChange: (tab: NcmDrawerTab) => void;
  onClose: () => void;
  onChanged: () => void;
  onRemovido: () => void;
  onFeedback: (message: string, ok: boolean) => void;
}

const dataHora = (iso: string): string =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function NcmDrawer({
  item,
  emissores,
  adotados,
  tab,
  onTabChange,
  onClose,
  onChanged,
  onRemovido,
  onFeedback,
}: Props): React.ReactElement {
  const [confirmarRemocao, setConfirmarRemocao] = useState(false);
  const [regraEditada, setRegraEditada] = useState(false);
  const [confirmarDescarte, setConfirmarDescarte] = useState(false);

  const pedirFechar = useCallback(() => {
    if (regraEditada) setConfirmarDescarte(true);
    else onClose();
  }, [regraEditada, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') pedirFechar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pedirFechar]);

  const uso = usoTotal(item);
  const semUso = uso === 0 && item.usos.naturezas === 0;
  const vinculos = item.usos.produtos + item.usos.resources + item.usos.estoques;

  const removerDaLista = async () => {
    setConfirmarRemocao(false);
    try {
      await ApiService.fiscal.removerNcmDaLista(item.ncmId);
      onFeedback(`${item.ncm} saiu da lista.`, true);
      onRemovido();
    } catch (error: unknown) {
      onFeedback(extractErrorMessage(error, 'Erro ao tirar o NCM da lista.'), false);
    }
  };

  return (
    <>
      <Scrim onClick={pedirFechar} />
      <Panel role="dialog" aria-label={`NCM ${item.ncm}`}>
        <Header>
          <HeaderTop>
            <div>
              <NcmCode>{item.ncm}</NcmCode>
              <Breadcrumb>
                Capítulo {item.capitulo} › posição {item.ncm.slice(0, 4)}
              </Breadcrumb>
            </div>
            <CloseButton type="button" title="Fechar" onClick={pedirFechar}>
              <X size={14} />
            </CloseButton>
          </HeaderTop>
          <Description>{item.descricao || '—'}</Description>
          <Chips>
            <Chip $tone={SITUACAO_TOM[item.situacao]}>{SITUACAO_LABEL[item.situacao]}</Chip>
            {uso > 0 ? <Chip $tone="info">{usoPartes(item).join(' · ')}</Chip> : <Chip $tone="mute">sem uso</Chip>}
            {item.bloqueiaVenda && <Chip $tone="bad">bloqueia venda</Chip>}
            {item.situacao === 'PARCIAL' && (
              <Chip $tone="warn">falta {item.emissoresSemRegra.map(emissorLabel).join(', ')}</Chip>
            )}
            {semUso && item.perfis.length > 0 && (
              <PushRightButton type="button" $danger onClick={() => setConfirmarRemocao(true)}>
                Tirar da lista
              </PushRightButton>
            )}
          </Chips>
          <Tabs>
            <Tab type="button" $active={tab === 'regra'} onClick={() => onTabChange('regra')}>
              Regra
            </Tab>
            <Tab type="button" $active={tab === 'vinculos'} onClick={() => onTabChange('vinculos')}>
              Vínculos <Count>{vinculos}</Count>
            </Tab>
          </Tabs>
        </Header>

        <Body>
          {/* A aba Regra fica montada: trocar para Vínculos não descarta o que se digitou. */}
          <div hidden={tab !== 'regra'}>
            <RuleTab
              key={item.ncmId}
              item={item}
              emissores={emissores}
              onSaved={onChanged}
              onFeedback={onFeedback}
              onDirtyChange={setRegraEditada}
            />
          </div>
          {tab === 'vinculos' && (
            <LinksTab key={item.ncmId} item={item} adotados={adotados} onChanged={onChanged} onFeedback={onFeedback} />
          )}
        </Body>

        {tab === 'regra' && (
          <Footer>
            {item.ultimaAlteracao
              ? `Alterada em ${dataHora(item.ultimaAlteracao.em)}${item.ultimaAlteracao.por ? ` por ${item.ultimaAlteracao.por.nome}` : ''}`
              : 'Sem regra cadastrada'}
          </Footer>
        )}
      </Panel>

      <ConfirmationModal
        isOpen={confirmarDescarte}
        title="Descartar alterações?"
        message="A regra editada ainda não foi salva. Fechar agora perde o que foi digitado."
        actions={[
          { label: 'Continuar editando', onClick: () => setConfirmarDescarte(false), variant: 'outline' },
          { label: 'Descartar', onClick: onClose, variant: 'danger' },
        ]}
      />

      <ConfirmationModal
        isOpen={confirmarRemocao}
        title={`Tirar ${item.ncm} da lista?`}
        message={
          `${item.perfis.length > 1 ? `As ${item.perfis.length} regras` : 'A regra'} da empresa para este NCM ${item.perfis.length > 1 ? 'são apagadas' : 'é apagada'}. O NCM continua na tabela oficial e volta à lista quando for usado ou ganhar regra.`
        }
        actions={[
          { label: 'Cancelar', onClick: () => setConfirmarRemocao(false), variant: 'outline' },
          { label: 'Tirar da lista', onClick: removerDaLista, variant: 'danger' },
        ]}
      />
    </>
  );
}
